"use client";

import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
  focusManager,
  onlineManager,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";

import {
  isNavigatorOnline,
  subscribeToNavigatorOnline,
} from "@/lib/pwa/network";
import { captureClientException } from "@/lib/analytics/capture-exception";
import { ExpectedUserError, isTransientNetworkError } from "@/lib/error-utils";
import { toast } from "@/hooks/ui/use-toast";
interface QueryProviderProps {
  children: React.ReactNode;
}

// The generic message the API route helpers return for an unhandled server
// exception. These 500s are captured server-side (captureServerException)
// with the real stack — capturing the symptom here too would double the
// PostHog issue, so the cache handlers skip client-side capture for it.
const SERVER_ERROR_MESSAGE = "Internal Server Error";

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "object" && error !== null && "message" in error) {
    const fallbackMessage = (error as { message?: unknown }).message;
    return typeof fallbackMessage === "string"
      ? fallbackMessage
      : String(fallbackMessage);
  }
  if (typeof error === "string") {
    return error;
  }
  return "Unknown error";
}

/** Whether a query or mutation failure belongs in PostHog. */
export function shouldCaptureCacheError(error: unknown): boolean {
  // Generic server-500s are captured server-side with the real stack.
  if (toErrorMessage(error) === SERVER_ERROR_MESSAGE) return false;
  // A conflict or precondition worded for the user is shown, not a defect.
  return !(error instanceof ExpectedUserError);
}

function createQueryClient(): QueryClient {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 30, // 30 minutes
        // Reads fast-fail on deterministic errors (4xx, PostgREST/Postgres
        // codes) but retry transient network blips — e.g. a Supabase `.rpc()`
        // POST landing on a stale keep-alive socket (ERR_CONNECTION_CLOSED),
        // which Chrome won't auto-retry because POST isn't idempotent.
        retry: (failureCount, error) =>
          failureCount < 2 && isTransientNetworkError(error),
        retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 3000),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: true,
      },
      mutations: {
        retry: false,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Only show toast for server errors (5xx) or unknown errors,
        // not for 404s or expected functional errors unless critical.
        const errorMessage = toErrorMessage(error);

        console.error(
          `Global Query Error [${JSON.stringify(query.queryKey)}]: ${errorMessage}`,
          error,
        );
        if (shouldCaptureCacheError(error)) {
          captureClientException(error, {
            scope: "tanstack-query",
            query_key: JSON.stringify(query.queryKey),
          });
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        // Capture every mutation failure — even ones with a local onError,
        // which would otherwise be swallowed before any tracker sees them.
        if (shouldCaptureCacheError(error)) {
          captureClientException(error, {
            scope: "tanstack-mutation",
            mutation_key: JSON.stringify(mutation.options.mutationKey ?? null),
          });
        }

        // If the mutation has its own onError, we can skip the global one if needed,
        // but usually we want a fallback toast.
        if (mutation.options.onError) {
          return;
        }

        const message =
          error instanceof Error ? error.message : "Something went wrong";
        toast({
          variant: "destructive",
          title: "Action Failed",
          description: message,
        });
      },
    }),
  });

  return queryClient;
}

export function QueryProvider({ children }: QueryProviderProps) {
  const [queryClient] = useState(() => createQueryClient());

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    focusManager.setEventListener((handleFocus) => {
      const listener = () => {
        handleFocus(document.visibilityState === "visible");
      };
      // eslint-disable-next-line @eslint-react/web-api-no-leaked-event-listener -- the listener lifecycle is owned by TanStack Query's focusManager.setEventListener, whose returned function (below) is the removeEventListener cleanup; it isn't the useEffect cleanup the rule looks for.
      document.addEventListener("visibilitychange", listener, false);
      return () => {
        document.removeEventListener("visibilitychange", listener);
      };
    });

    onlineManager.setEventListener((setOnline) => {
      const unsubscribe = subscribeToNavigatorOnline(() => {
        setOnline(isNavigatorOnline());
      });
      setOnline(isNavigatorOnline());
      return () => {
        unsubscribe();
      };
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
